import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export type OperationType = 'PURCHASE' | 'PROCESSING' | 'COMMISSION' | 'IMPORT';

export interface GoodsReceiptItem {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  series_id?: string | null;
  characteristic_id?: string | null;
  quantity: string;
  uom_id?: string | null;
  price: string;
  amount_without_vat: string;
  discount_percent?: string;
  discount_amount?: string;
  vat_rate_id: string;
  vat_amount: string;
  total_amount: string;
  project_id?: string | null;
  /** ПДВ-призначення (для класифікації ВК — ПКУ ст.199). */
  tax_purpose_id?: string | null;
}

export interface GoodsReceiptListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  warehouse_id: string;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  project_id?: string | null;
  // Block 5.3b:
  state?: CancellableState;
}

export interface GoodsReceipt extends GoodsReceiptListItem {
  operation_type: OperationType;
  contract_id: string;
  supplier_order_id: string | null;
  supplier_doc_number: string | null;
  supplier_doc_date: string | null;
  prices_include_vat: boolean;
  amount_without_vat: string;
  vat_amount: string;
  posted_at: string | null;
  comment: string | null;
  items: GoodsReceiptItem[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface GoodsReceiptCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  operation_type?: OperationType;
  contractor_id: string;
  /** 2026-06-02: nullable — backend auto-create default contract. */
  contract_id: string | null;
  warehouse_id: string;
  supplier_order_id?: string | null;
  supplier_doc_number?: string | null;
  supplier_doc_date?: string | null;
  prices_include_vat?: boolean;
  comment?: string | null;
  items: GoodsReceiptItem[];
  project_id?: string | null;
}

export type GoodsReceiptUpdate = Partial<GoodsReceiptCreate>;

export const goodsReceipts = {
  list: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean; limit?: number }) =>
    api.get<Page<GoodsReceiptListItem>>('/goods-receipts', { params }).then((r) => r.data),
  get: (id: string) => api.get<GoodsReceipt>(`/goods-receipts/${id}`).then((r) => r.data),
  create: (payload: GoodsReceiptCreate) =>
    api.post<GoodsReceipt>('/goods-receipts', payload).then((r) => r.data),
  update: (id: string, payload: GoodsReceiptUpdate) =>
    api.patch<GoodsReceipt>(`/goods-receipts/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/goods-receipts/${id}`).then(() => undefined),
  post: (id: string) => api.post<GoodsReceipt>(`/goods-receipts/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<GoodsReceipt>(`/goods-receipts/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/goods-receipts/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
